import { Layout, Menu } from "antd";
import type { MenuProps } from "antd";
import React from "react";
import styles from "./styles.module.scss";
import { Link, useLocation } from "react-router-dom";
import {
  UserOutlined,
  BankOutlined,
  BookOutlined,
  InfoOutlined,
  StarOutlined,
  FileAddOutlined,
  CoffeeOutlined,
  TrophyOutlined,
  CalendarOutlined,
  BookOutlined as DiaryOutlined,
  BugOutlined,
  RocketOutlined,
  PlayCircleOutlined,
  AppstoreOutlined,
  DollarOutlined,
  HistoryOutlined,
  RestOutlined,
  PartitionOutlined,
  HomeOutlined,
  SettingOutlined,
  CloudDownloadOutlined,
  AlertOutlined,
  SolutionOutlined,
  ExperimentOutlined,
  SafetyCertificateOutlined,
  AuditOutlined,
  DatabaseOutlined,
  SwapOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import emmiLogo from "../../../assets/emmi_logo.png"; // Importar la imagen
import GraduationCapIcon from "../../../pages/App/Formacion/GraduationCapIcon";
import { ROUTE_TITLES } from "../menu-items";
import { IS_SUPER_ADMIN } from "../../../store/selectors";

const { Sider } = Layout;

interface SiderAppProps {
  collapsed: boolean;
}

export const SiderApp: React.FC<SiderAppProps> = ({ collapsed }) => {
  const location = useLocation(); // Obtener la ubicación actual
  const isSuperAdmin = useSelector(IS_SUPER_ADMIN);

  // Obtener la ruta base (primer segmento después del dominio)
  const basePath = `/${location.pathname.split("/")[1]}`;

  type MenuItem = Required<MenuProps>["items"][number];

  const getItem = (
    label?: React.ReactNode,
    key?: React.Key,
    icon?: React.ReactNode,
    item?: React.ReactNode,
  ): MenuItem => {
    return {
      key,
      icon,
      item,
      label,
    } as MenuItem;
  };

  // Group items are always-visible section headers (not collapsible
  // SubMenus) — antd renders `type: "group"` items as a label followed by
  // its children, all rendered inline in the menu.
  const getGroup = (label: React.ReactNode, children: MenuItem[]): MenuItem => {
    return {
      type: "group",
      label,
      children,
    } as MenuItem;
  };

  const items: MenuItem[] = [
    getItem(
      ROUTE_TITLES["/"],
      "/",
      <Link to="/">
        <HomeOutlined />
      </Link>,
    ),
    getGroup("Contenido", [
      getItem(
        ROUTE_TITLES["/programs"],
        "/programs",
        <Link to="/programs">
          <BookOutlined />
        </Link>,
      ),
      getItem(
        ROUTE_TITLES["/courses"],
        "/courses",
        <Link to="/courses">
          <BookOutlined />
        </Link>,
      ),
      getItem(
        ROUTE_TITLES["/lessons"],
        "/lessons",
        <Link to="/lessons">
          <StarOutlined />
        </Link>,
      ),
      getItem(
        ROUTE_TITLES["/resources"],
        "/resources",
        <Link to="/resources">
          <FileAddOutlined />
        </Link>,
      ),
      getItem(
        ROUTE_TITLES["/mapa-contenido"],
        "/mapa-contenido",
        <Link to="/mapa-contenido">
          <PartitionOutlined />
        </Link>,
      ),
      getItem(
        ROUTE_TITLES["/atencion"],
        "/atencion",
        <Link to="/atencion">
          <AlertOutlined />
        </Link>,
      ),
      getItem(
        ROUTE_TITLES["/game-quizzes"],
        "/game-quizzes",
        <Link to="/game-quizzes">
          <TrophyOutlined />
        </Link>,
      ),
      getItem(
        ROUTE_TITLES["/quiz-retrieval"],
        "/quiz-retrieval",
        <Link to="/quiz-retrieval">
          <TrophyOutlined />
        </Link>,
      ),
      getItem(
        ROUTE_TITLES["/attempts"],
        "/attempts",
        <Link to="/attempts">
          <SolutionOutlined />
        </Link>,
      ),
      getItem(
        ROUTE_TITLES["/recursos-prueba"],
        "/recursos-prueba",
        <Link to="/recursos-prueba">
          <ExperimentOutlined />
        </Link>,
      ),
      getItem(
        ROUTE_TITLES["/revision-ods"],
        "/revision-ods",
        <Link to="/revision-ods">
          <AuditOutlined />
        </Link>,
      ),
    ]),
    getGroup("Escuelas y usuarios", [
      getItem(
        ROUTE_TITLES["/users"],
        "/users",
        <Link to="/users">
          <UserOutlined />
        </Link>,
      ),
      getItem(
        ROUTE_TITLES["/schools"],
        "/schools",
        <Link to="/schools">
          <BankOutlined />
        </Link>,
      ),
      getItem(
        ROUTE_TITLES["/subjects"],
        "/subjects",
        <Link to="/subjects">
          <InfoOutlined />
        </Link>,
      ),
      getItem(
        ROUTE_TITLES["/grades"],
        "/grades",
        <Link to="/grades">
          <CoffeeOutlined />
        </Link>,
      ),
      getItem(
        ROUTE_TITLES["/calendar"],
        "/calendar",
        <Link to="/calendar">
          <CalendarOutlined />
        </Link>,
      ),
      getItem(
        ROUTE_TITLES["/diary-teacher"],
        "/diary-teacher",
        <Link to="/diary-teacher">
          <DiaryOutlined />
        </Link>,
      ),
      getItem(
        ROUTE_TITLES["/formacion"],
        "/formacion",
        <Link to="/formacion">
          <GraduationCapIcon />
        </Link>,
      ),
    ]),
    getGroup("Comercio", [
      getItem(
        ROUTE_TITLES["/tienda-paquetes"],
        "/tienda-paquetes",
        <Link to="/tienda-paquetes">
          <AppstoreOutlined />
        </Link>,
      ),
      getItem(
        ROUTE_TITLES["/ventas"],
        "/ventas",
        <Link to="/ventas">
          <DollarOutlined />
        </Link>,
      ),
    ]),
    getGroup("Herramientas", [
      getItem(
        ROUTE_TITLES["/subidor-supersonico"],
        "/subidor-supersonico",
        <Link to="/subidor-supersonico">
          <RocketOutlined />
        </Link>,
      ),
      getItem(
        ROUTE_TITLES["/hub-subida"],
        "/hub-subida",
        <Link to="/hub-subida">
          <SwapOutlined rotate={90} />
        </Link>,
      ),
      getItem(
        ROUTE_TITLES["/game-diagnostics"],
        "/game-diagnostics",
        <Link to="/game-diagnostics">
          <BugOutlined />
        </Link>,
      ),
      getItem(
        ROUTE_TITLES["/demo-provision"],
        "/demo-provision",
        <Link to="/demo-provision">
          <PlayCircleOutlined />
        </Link>,
      ),
      getItem(
        ROUTE_TITLES["/papelera"],
        "/papelera",
        <Link to="/papelera">
          <RestOutlined />
        </Link>,
      ),
      getItem(
        ROUTE_TITLES["/actividad"],
        "/actividad",
        <Link to="/actividad">
          <HistoryOutlined />
        </Link>,
      ),
      getItem(
        ROUTE_TITLES["/consultas"],
        "/consultas",
        <Link to="/consultas">
          <DatabaseOutlined />
        </Link>,
      ),
      getItem(
        ROUTE_TITLES["/progreso-profesores"],
        "/progreso-profesores",
        <Link to="/progreso-profesores">
          <BarChartOutlined />
        </Link>,
      ),
      // Access management is reserved for "administradores principales"
      // (SCRUM-706). Hiding the entry is cosmetic — SuperAdminRoute gates the
      // route and SuperAdminGuard gates the API.
      ...(isSuperAdmin
        ? [
            getItem(
              ROUTE_TITLES["/accesos"],
              "/accesos",
              <Link to="/accesos">
                <SafetyCertificateOutlined />
              </Link>,
            ),
          ]
        : []),
      getItem(
        ROUTE_TITLES["/version-app"],
        "/version-app",
        <Link to="/version-app">
          <CloudDownloadOutlined />
        </Link>,
      ),
      getItem(
        ROUTE_TITLES["/settings"],
        "/settings",
        <Link to="/settings">
          <SettingOutlined />
        </Link>,
      ),
    ]),
    // Logout moved to the profile modal in the Header (approved decision).
  ];

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      className={styles.container}
    >
      <div className={styles.imageContainerClose}>
        <img src={emmiLogo} alt="Emmi Logo" style={{ maxWidth: "100px" }} />
      </div>
      <Menu
        selectedKeys={[basePath]} // Seleccionar el elemento basado en la ruta base
        items={items}
        className={styles.containerSider}
        itemType={styles.buttonMenu}
      />
    </Sider>
  );
};
